

import { FC, useState, useEffect } from 'react';
import { useSupabase } from '../../hooks/useSupabase';

interface HeroSlide {
  id: number;
  image: string;
  title: string;
  subtitle: string;
}

const HeroSection: FC = () => {
  const { supabase } = useSupabase();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState<HeroSlide[]>([
    {
      id: 1,
      image: 'https://images.unsplash.com/photo-1544025162-d76694265947?w=1920&h=1080&fit=crop&crop=center',
      title: '30년 경력 셰프의 정성',
      subtitle: '최상급 재료로 만드는 프리미엄 한식'
    },
    {
      id: 2,
      image: 'https://images.unsplash.com/photo-1529692236671-f1f6cf9683ba?w=1920&h=1080&fit=crop&crop=center',
      title: '최상급 이베리코 흑돼지',
      subtitle: '스페인 직수입 프리미엄 돼지고기'
    },
    {
      id: 3,
      image: 'https://images.unsplash.com/photo-1498654896293-37aacf113fd9?w=1920&h=1080&fit=crop&crop=center',
      title: '즉석 가마솥밥',
      subtitle: '주문 즉시 지어드리는 따뜻한 밥'
    },
    {
      id: 4,
      image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1920&h=1080&fit=crop&crop=center',
      title: '넓고 편안한 95석 공간',
      subtitle: '혼밥부터 가족 외식까지'
    }
  ]);

  // 데이터베이스에서 슬라이드 로드
  useEffect(() => {
    loadSlides();
  }, [supabase]);

  const loadSlides = async () => {
    if (!supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('hero_slides')
        .select('*')
        .order('id');

      if (error && error.code !== 'PGRST116') {
        console.error('슬라이드 로드 실패:', error);
        return;
      }

      if (data && data.length > 0) {
        setSlides(data);
      }
    } catch (error) {
      console.error('슬라이드 로드 중 오류:', error);
    }
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const scrollToReservation = () => {
    const element = document.getElementById('reservation');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative h-screen overflow-hidden">
      {/* Background Slides */}
      <div className="absolute inset-0">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover object-center"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = 'https://images.unsplash.com/photo-1544025162-d76694265947?w=1920&h=1080&fit=crop&crop=center';
              }}
            />
            <div className="absolute inset-0 bg-black/40"></div>
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              815광복점
            </h1>
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl text-white/90 mb-4 transition-all duration-500">
                {slides[currentSlide]?.title}
              </h2>
              <p className="text-lg md:text-xl text-white/80 transition-all duration-500">
                {slides[currentSlide]?.subtitle}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={scrollToReservation}
                className="bg-gradient-to-r from-red-600 to-blue-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-line mr-2"></i>
                예약하기
              </button>
              <button
                onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-gray-800 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-down-line mr-2"></i>
                더 알아보기
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all cursor-pointer ${
              index === currentSlide ? 'bg-white' : 'bg-white/50'
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSection;

